import React from 'react';

/**
 * Button Component - Touch-friendly, accessible button with GCHL brand variants
 *
 * @param {Object} props
 * @param {'primary'|'action'|'secondary'|'danger'|'ghost'|'success'} props.variant - Button style variant
 * @param {'sm'|'md'|'lg'} props.size - Button size
 * @param {boolean} props.loading - Show loading state
 * @param {boolean} props.disabled - Disable button
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.children - Button content
 * @param {Function} props.onClick - Click handler
 * @param {string} props.type - Button type (button, submit, reset)
 */
const Button = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  className = '',
  children,
  onClick,
  type = 'button',
  ...props
}) => {
  // Base styles - touch-friendly minimum height with brand fonts
  const baseStyles = `
    inline-flex items-center justify-center
    font-display font-semibold
    rounded-lg
    transition-all duration-200
    active:scale-95
    disabled:opacity-50 disabled:cursor-not-allowed
    focus:outline-none focus:ring-2 focus:ring-offset-2
  `;

  // Variant styles - GCHL Brand System with on-brand hover/focus states
  const variantStyles = {
    // Primary: Forest Green - Headers, navigation
    primary: `
      bg-forest-950 text-white
      hover:bg-forest-900
      focus:ring-forest-500
      shadow-md hover:shadow-lg
      dark:bg-gold-400 dark:text-navy-900
      dark:hover:bg-gold-300
      dark:focus:ring-gold-400
    `,

    // Action: Gold - Primary CTAs (Post Score, Submit)
    action: `
      bg-gold-400 text-navy-900
      hover:bg-gold-300
      focus:ring-gold-500
      shadow-md hover:shadow-lg
      font-bold
    `,

    // Secondary: Outlined - Alternative actions
    secondary: `
      bg-white text-forest-950
      border-2 border-forest-950
      hover:bg-forest-50
      focus:ring-forest-500
      dark:bg-navy-900 dark:text-gold-400
      dark:border-gold-400
      dark:hover:bg-navy-800
      dark:focus:ring-gold-400
    `,

    // Danger: Sunset Orange - Destructive actions
    danger: `
      bg-sunset-light text-white
      hover:bg-sunset-dark
      focus:ring-sunset-light
      shadow-md hover:shadow-lg
      dark:bg-sunset-dark
      dark:hover:bg-orange-600
      dark:focus:ring-sunset-dark
    `,

    // Ghost: Transparent - Subtle actions
    ghost: `
      bg-transparent text-navy-900
      hover:bg-gray-100
      focus:ring-forest-500
      dark:text-navy-100
      dark:hover:bg-navy-800
      dark:focus:ring-gold-400
    `,

    // Success: Seafoam Teal - Positive confirmations
    success: `
      bg-seafoam-light text-white
      hover:bg-seafoam-dark
      focus:ring-seafoam-light
      shadow-md hover:shadow-lg
      dark:bg-seafoam-dark
      dark:hover:bg-teal-400
      dark:focus:ring-seafoam-dark
    `,
  };

  // Size styles - all meet 44px minimum touch target
  const sizeStyles = {
    sm: 'px-4 py-2 text-sm min-h-touch',      // 44px min height
    md: 'px-6 py-3 text-base min-h-touch',    // 44px min height
    lg: 'px-8 py-4 text-lg min-h-touch-lg',   // 52px min height
  };

  const combinedClassName = `
    ${baseStyles}
    ${variantStyles[variant] || variantStyles.primary}
    ${sizeStyles[size] || sizeStyles.md}
    ${className}
  `.replace(/\s+/g, ' ').trim();

  return (
    <button
      type={type}
      className={combinedClassName}
      onClick={onClick}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </button>
  );
};

export default Button;
